import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { targetAPI, analyticsAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { Input } from '../ui/input';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { PageFilters, YearFilter, QuarterFilter, SalesRepFilter, AccountFilter, StageFilter, ProductDirectorFilter, SolutionCategoryFilter } from '../layout/PageFilters';
import { RoleScopeControl } from '../layout/RoleScopeControl';
import {
  Target, TrendingUp, DollarSign, Trophy, AlertTriangle, Building2,
  Users, BarChart2, Activity, RefreshCw, Layers,
  Search, ExternalLink, ArrowRight, X, Play, Pause, Download
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell, AreaChart, Area, RadialBarChart, RadialBar, PolarGrid } from 'recharts';
import { toast } from 'sonner';
import { useCurrency } from '../../lib/CurrencyContext';
import { formatCurrencyCompact, formatNumberCompact } from '../../lib/currency';
import { DashboardSessionTabs } from './DashboardSessionTabs';
import { SOPipelineHealthCard } from './SOPipelineHealthCard';
import { InvoiceCollectionHealthCard, DealVelocityCard, WinLossIntelligenceCard, ActivityCadenceHealthCard } from './HealthCards';
import { CustomDashboardTab } from './CustomDashboardTab';
import { SalesPerformanceCard } from './SalesPerformanceCard';
import { FinanceOpsCard } from './FinanceOpsCard';
import { PipelineCommitmentCard } from './PipelineCommitmentCard';

// Maps health_card_type to the React component
function HealthCardRenderer({ type, filters }) {
  switch (type) {
    case 'so_pipeline':        return <SOPipelineHealthCard filters={filters} />;
    case 'invoice_collection': return <InvoiceCollectionHealthCard filters={filters} />;
    case 'deal_velocity':      return <DealVelocityCard filters={filters} />;
    case 'win_loss':           return <WinLossIntelligenceCard filters={filters} />;
    case 'activity_cadence':   return <ActivityCadenceHealthCard filters={filters} />;
    case 'sales_performance':  return <SalesPerformanceCard filters={filters} />;
    case 'finance_operations': return <FinanceOpsCard filters={filters} />;
    case 'pipeline_commitment': return <PipelineCommitmentCard filters={filters} />;
    default: return <div className="h-full flex items-center justify-center text-muted-foreground text-sm rounded-2xl border">Unknown health card: {type}</div>;
  }
}

const ICONS = { Target, DollarSign, TrendingUp, Trophy, AlertTriangle, Building2, Users, BarChart2, Activity };
const CHART_COLORS = ['#800000', '#3b82f6', '#10b981', '#f59e0b', '#6366f1', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];
const AVATAR_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

const CARD_COLORS = {
  '#1e3a5f': { muted: '#8bb4e0', iconBg: 'rgba(255,255,255,0.12)' },
  '#8b1a1a': { muted: '#e8a0a0', iconBg: 'rgba(255,255,255,0.12)' },
  '#1a6b4a': { muted: '#8fd4b4', iconBg: 'rgba(255,255,255,0.12)' },
  '#b45309': { muted: '#fbbf6e', iconBg: 'rgba(255,255,255,0.12)' },
  '#5b21b6': { muted: '#c4a8ec', iconBg: 'rgba(255,255,255,0.12)' },
  '#0e7490': { muted: '#7dd3e8', iconBg: 'rgba(255,255,255,0.12)' },
  '#800000': { muted: '#d4a0a0', iconBg: 'rgba(255,255,255,0.12)' },
  '#3730a3': { muted: '#a5b4fc', iconBg: 'rgba(255,255,255,0.12)' },
  '#4d5e2f': { muted: '#b8cc94', iconBg: 'rgba(255,255,255,0.12)' },
  '#334155': { muted: '#94a3b8', iconBg: 'rgba(255,255,255,0.12)' },
  '#3b82f6': { muted: '#93c5fd', iconBg: 'rgba(255,255,255,0.12)' },
};
const getColorInfo = (c) => CARD_COLORS[c] || { muted: 'rgba(255,255,255,0.7)', iconBg: 'rgba(255,255,255,0.12)' };

// ============ DRILL-DOWN PANEL ============
function DrillDownPanel({ open, onClose, card, year, onNavigate }) {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !card?.id) return;
    setSearch('');
    (async () => {
      setLoading(true);
      try { const r = await targetAPI.drillDownCard(card.id, { year, limit: 100 }); setRecords(r.data.records || []); setTotal(r.data.total || 0); } catch {}
      finally { setLoading(false); }
    })();
  }, [open, card?.id, year]);

  const filtered = records.filter(r => !search || Object.values(r).some(v => String(v).toLowerCase().includes(search.toLowerCase())));
  const columns = records.length > 0 ? Object.keys(records[0]).filter(k => !k.startsWith('canonical') && !k.startsWith('source_record') && k !== 'deleted') : [];
  const fmtH = k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const fmtV = (k, v) => { if (v == null) return '-'; if (k.includes('value') || k.includes('amount')) return formatCurrencyCompact(Number(v)); if (k.includes('date') && typeof v === 'string') return v.split('T')[0]; return String(v); };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[700px] sm:w-[800px] sm:max-w-[800px] p-0" data-testid="drill-down-panel">
        <div className="flex flex-col h-full">
          <SheetHeader className="px-6 py-4 border-b" style={{ backgroundColor: card?.color || '#1e3a5f' }}>
            <SheetTitle className="text-white">
              <span className="text-lg font-bold">{card?.name || 'Records'}</span>
              <Badge className="ml-2 bg-white/20 text-white border-0">{total} records</Badge>
            </SheetTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search records..."
                className="pl-9 h-9 text-sm bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/20" />
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1 bg-gray-50">
            {loading ? <div className="p-4 space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
            : filtered.length === 0 ? <div className="p-12 text-center text-gray-400"><Target className="h-12 w-12 mx-auto mb-3 text-gray-300" /><p>No records found</p></div>
            : <div className="p-4 space-y-2">{filtered.map((r, idx) => {
                const mainVal = r.sale_value || r.amount_total || 0;
                const name = r.name || r.invoice_number || r.summary || Object.values(r)[0];
                return (
                  <div key={idx} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-gray-200 transition-all cursor-pointer" data-testid={`drill-down-row-${idx}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">{name}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                          {columns.slice(1, 6).map(c => {
                            const val = fmtV(c, r[c]);
                            if (val === '-') return null;
                            return (
                              <div key={c} className="flex items-center gap-1.5">
                                <span className="text-[10px] text-gray-400 uppercase tracking-wide">{fmtH(c)}</span>
                                <span className="text-xs font-medium text-gray-700 bg-gray-50 px-1.5 py-0.5 rounded">{val}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {mainVal > 0 && (
                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold text-[#800000]">{formatCurrencyCompact(mainVal)}</p>
                          {r.probability && <p className="text-[10px] text-gray-400">{r.probability}% prob.</p>}
                        </div>
                      )}
                    </div>
                    {r.stage && (
                      <div className="mt-2 pt-2 border-t border-gray-50">
                        <Badge variant="outline" className="text-[10px]">{r.stage}</Badge>
                        {r.owner_name && <Badge variant="outline" className="text-[10px] ml-1">{r.owner_name}</Badge>}
                        {r.product_manager && <Badge variant="outline" className="text-[10px] ml-1">{r.product_manager}</Badge>}
                      </div>
                    )}
                  </div>
                );
              })}</div>}
          </ScrollArea>
          <div className="px-6 py-3 border-t bg-white flex items-center justify-between text-xs text-gray-500">
            <span>Showing {filtered.length} of {total}</span>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">{card?.collection}</span>
              {onNavigate && <Button variant="outline" size="sm" onClick={() => { onNavigate(card); onClose(); }} className="h-7 text-xs"><ArrowRight className="h-3 w-3 mr-1" /> View All in Page</Button>}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============ KPI CARD (Read-only, Odoo-style with comparison) ============
function KpiCard({ card, data, prevPeriod, onDrillDown, onNavigate }) {
  const Icon = ICONS[card?.icon] || Target;
  const groups = data?.groups || [];
  const bg = card?.color || '#1e3a5f';
  const ci = getColorInfo(bg);

  // Compute gradient colors from base color — no dynamic Tailwind (gets purged!)
  const gradientStyle = (hex) => {
    const darken = (h, amt) => {
      const n = parseInt(h.slice(1), 16);
      const r = Math.max(0, (n >> 16) - amt);
      const g = Math.max(0, ((n >> 8) & 0xff) - amt);
      const b = Math.max(0, (n & 0xff) - amt);
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    };
    const lighten = (h, amt) => {
      const n = parseInt(h.slice(1), 16);
      const r = Math.min(255, (n >> 16) + amt);
      const g = Math.min(255, ((n >> 8) & 0xff) + amt);
      const bl = Math.min(255, (n & 0xff) + amt);
      return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0')}`;
    };
    // Special cases for better gradients
    const presets = {
      '#800000': ['#800000', '#c0392b'],
      '#ef4444': ['#ef4444', '#dc2626'],
      '#10b981': ['#059669', '#0d9488'],
      '#059669': ['#059669', '#0d9488'],
      '#3b82f6': ['#3b82f6', '#4f46e5'],
      '#2563eb': ['#2563eb', '#4f46e5'],
      '#f59e0b': ['#f59e0b', '#ea580c'],
      '#8b5cf6': ['#8b5cf6', '#7c3aed'],
      '#0891b2': ['#0891b2', '#2563eb'],
      '#1e3a5f': ['#1e3a5f', '#1e40af'],
    };
    const [from, to] = presets[hex] || [hex, darken(hex, 30)];
    return { background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)` };
  };

  const displayValue = () => {
    if (card.data_calculation && card.data_calculation !== 'none' && data?.value !== undefined) {
      const val = parseFloat(data.value) || 0;
      if (card.data_calculation === 'percentage') return val.toFixed(1) + '%';
      if (card.data_calculation === 'ratio') return val.toFixed(3);
      return formatNumberCompact(val);
    }
    if (card.display_type === 'win_rate') {
      const won = groups.find(g => g.label === 'Won')?.count || 0;
      const lost = groups.find(g => g.label === 'Lost')?.count || 0;
      return ((won / Math.max(won + lost, 1)) * 100).toFixed(0) + '%';
    }
    const val = data?.value ?? 0;
    if (card.aggregation === 'sum' || card.aggregation === 'avg') {
      return formatNumberCompact(val);
    }
    return formatNumberCompact(val);
  };

  const changePct = prevPeriod?.change_pct;
  const hasChange = changePct !== undefined && changePct !== null && changePct !== 0;

  return (
    <div
      className="relative overflow-hidden h-full rounded-2xl cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 group"
      style={{ ...gradientStyle(bg), boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
      onClick={() => onNavigate?.(card)}
      data-testid={`kpi-card-${card.id}`}
    >
      {/* Decorative circles */}
      <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full" style={{ background: 'rgba(255,255,255,0.12)' }} />
      <div className="absolute -right-2 top-10 w-14 h-14 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
      
      <div className="relative z-10 p-5 flex flex-col h-full">
        <div className="flex items-start justify-between mb-3">
          <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest leading-none">{card.name}</p>
          <div className="p-1.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.18)' }}>
            <Icon className="h-3.5 w-3.5 text-white" />
          </div>
        </div>
        <p className="text-3xl font-black text-white tracking-tight leading-none drop-shadow-sm">{displayValue()}</p>
        {hasChange && (
          <div className="flex items-center gap-1 mt-2 text-xs font-semibold text-white/80">
            {changePct > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingUp className="h-3 w-3 rotate-180" />}
            <span>{changePct > 0 ? '+' : ''}{changePct}%</span>
            <span className="text-white/40 font-normal">vs {prevPeriod?.year}</span>
          </div>
        )}
        {!hasChange && data?.count > 0 && card.display_type !== 'win_rate' && (
          <p className="text-[10px] mt-2 text-white/50">{data.count} records</p>
        )}
      </div>
    </div>
  );
}

// ============ CHART CARD (Read-only, shadcn charts) ============
function ChartCard({ card, data, onDrillDown, onNavigate, onItemNavigate }) {
  const groups = data?.groups || [];
  if (!card) return null;

  // Navigate with specific group filter (e.g., click "Nabisaheb" → opportunities?salesRep=Nabisaheb)
  const handleItemClick = (groupLabel) => {
    if (!onItemNavigate || !card || !groupLabel) return;
    onItemNavigate(card, groupLabel);
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-2xl border border-slate-100 overflow-hidden transition-all duration-200 hover:shadow-md" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div className="px-5 pt-4 pb-1 flex items-center justify-between border-b border-slate-50">
        <h4 className="text-sm font-semibold text-slate-800 tracking-tight">{card.name}</h4>
        <div className="flex gap-1">
          {onNavigate && <button onClick={() => onNavigate(card)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" title="Open in page"><ArrowRight className="h-3.5 w-3.5 text-slate-400" /></button>}
          {onDrillDown && <button onClick={() => onDrillDown(card)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" title="Drill down"><ExternalLink className="h-3.5 w-3.5 text-slate-400" /></button>}
        </div>
      </div>
      <div className="flex-1 min-h-0 px-2 pb-2">
        {card.display_type === 'chart' && (() => {
          const dk = card.aggregation === 'count' ? 'count' : 'total';
          const cfg = {}; groups.slice(0, 12).forEach((g, i) => { cfg[g.label || `i${i}`] = { label: g.label, color: CHART_COLORS[i % CHART_COLORS.length] }; }); cfg[dk] = { label: card.name };
          return (
            <ChartContainer config={cfg} className="h-full w-full">
              <BarChart accessibilityLayer data={groups.slice(0, 12)} margin={{ left: -10, bottom: 20, right: 10 }}
                onClick={(e) => { if (e?.activeLabel) handleItemClick(e.activeLabel); }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : v} />
                <ChartTooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} content={<ChartTooltipContent formatter={(v) => formatCurrencyCompact(v)} />} />
                <Bar dataKey={dk} radius={[6, 6, 0, 0]} className="cursor-pointer">{groups.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Bar>
              </BarChart>
            </ChartContainer>);
        })()}
        {card.display_type === 'pie' && (() => {
          const dk = card.aggregation === 'count' ? 'count' : 'total';
          const cfg = {}; groups.slice(0, 8).forEach((g, i) => { cfg[g.label || `s${i}`] = { label: g.label, color: CHART_COLORS[i % CHART_COLORS.length] }; });
          return (
            <div className="flex items-center justify-center gap-4 h-full w-full min-h-0">
              <ChartContainer config={cfg} className="h-full flex-shrink-0" style={{width: '45%', minWidth: '100px'}}>
                <PieChart><ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                  <Pie data={groups.slice(0, 8)} dataKey={dk} nameKey="label" cx="50%" cy="50%" innerRadius="30%" outerRadius="68%" paddingAngle={2}
                    onClick={(_, idx) => handleItemClick(groups[idx]?.label)}>
                    {groups.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} className="cursor-pointer" />)}
                  </Pie>
                </PieChart>
              </ChartContainer>
              <div className="flex-1 space-y-1.5 overflow-y-auto min-w-0 max-h-full">
                {groups.slice(0, 8).map((g, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 rounded px-1.5 py-1" onClick={() => handleItemClick(g.label)}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="text-gray-600 truncate flex-1 min-w-0">{g.label || '-'}</span>
                    <span className="font-mono font-semibold text-gray-800 shrink-0">{formatNumberCompact(g.total || g.count || 0)}</span>
                  </div>
                ))}
              </div>
            </div>);
        })()}
        {card.display_type === 'area' && (() => {
          const dk = card.aggregation === 'count' ? 'count' : 'total';
          const cfg = {}; groups.slice(0, 12).forEach((g, i) => { cfg[g.label || `i${i}`] = { label: g.label, color: CHART_COLORS[i % CHART_COLORS.length] }; }); cfg[dk] = { label: card.name };
          return (
            <ChartContainer config={cfg} className="h-full w-full">
              <AreaChart accessibilityLayer data={groups.slice(0, 12)} margin={{ left: -10, bottom: 20, right: 10 }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : v} />
                <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                <defs><linearGradient id="fillArea" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#800000" stopOpacity={0.8}/><stop offset="95%" stopColor="#800000" stopOpacity={0.1}/></linearGradient></defs>
                <Area dataKey={dk} type="monotone" fill="url(#fillArea)" stroke="#800000" strokeWidth={2} />
              </AreaChart>
            </ChartContainer>);
        })()}
        {card.display_type === 'radial' && (() => {
          const dk = card.aggregation === 'count' ? 'count' : 'total';
          const cfg = {}; const radialData = groups.slice(0, 6).map((g, i) => ({ ...g, fill: CHART_COLORS[i % CHART_COLORS.length] }));
          radialData.forEach((g, i) => { cfg[g.label || `s${i}`] = { label: g.label, color: CHART_COLORS[i % CHART_COLORS.length] }; });
          return (
            <div className="flex items-center gap-3 h-full">
              <ChartContainer config={cfg} className="h-full w-1/2">
                <RadialBarChart data={radialData} innerRadius="20%" outerRadius="90%" startAngle={180} endAngle={0}>
                  <PolarGrid gridType="circle" radialLines={false} stroke="none" polarRadius={[56, 44]} />
                  <RadialBar dataKey={dk} background cornerRadius={6} />
                  <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel nameKey="label" />} />
                </RadialBarChart>
              </ChartContainer>
              <div className="space-y-1 flex-1 overflow-auto">{radialData.map((g, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5" onClick={() => handleItemClick(g.label)}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: g.fill }} />
                  <span className="text-gray-500 truncate flex-1">{g.label || '-'}</span>
                  <span className="font-mono font-semibold text-gray-700">{formatNumberCompact(g.total || g.count || 0)}</span>
                </div>))}</div>
            </div>);
        })()}
        {card.display_type === 'leaderboard' && (
          <div className="px-3 space-y-1 overflow-auto h-full">{groups.slice(0, 10).map((g, idx) => (
            <div key={idx} className="flex items-center gap-2.5 hover:bg-gray-50 rounded-lg px-2 py-1.5 transition-colors cursor-pointer"
              onClick={() => handleItemClick(g.label)} data-testid={`leaderboard-item-${idx}`}>
              <span className="text-xs font-bold text-gray-400 w-5 text-center shrink-0">{idx + 1}</span>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0" style={{ backgroundColor: AVATAR_COLORS[idx % AVATAR_COLORS.length] }}>
                {(g.label || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}</div>
              <span className="flex-1 text-xs font-medium text-gray-700 truncate min-w-0">{g.label || '-'}</span>
              <span className="text-xs font-bold text-[#800000] whitespace-nowrap shrink-0 tabular-nums">{formatCurrencyCompact(g.total || 0)}</span>
            </div>))}</div>
        )}
        {card.display_type === 'progress' && (
          <div className="px-2 space-y-2 overflow-auto h-full">{(() => { const mx = Math.max(...groups.map(g => g.total || g.count || 0), 1);
            return groups.slice(0, 8).map((g, i) => (<div key={i} className="cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5" onClick={() => handleItemClick(g.label)}>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-gray-600 truncate flex-1">{g.label || '-'}</span><span className="font-semibold text-gray-900 ml-2">{formatCurrencyCompact(g.total || 0)}</span></div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${((g.total || g.count || 0) / mx) * 100}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} /></div></div>));
          })()}</div>
        )}
        {card.display_type === 'table' && (
          <div className="overflow-auto h-full">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#3db5a4] text-white">
                  {card.collection === 'opportunities' && <><th className="px-3 py-2 text-left font-semibold">Customer</th><th className="px-3 py-2 text-left font-semibold">Deal Name</th><th className="px-3 py-2 text-right font-semibold">Sale Value</th><th className="px-3 py-2 text-left font-semibold">Salesperson</th></>}
                  {card.collection === 'invoices' && <><th className="px-3 py-2 text-left font-semibold">Customer</th><th className="px-3 py-2 text-left font-semibold">Invoice</th><th className="px-3 py-2 text-right font-semibold">Amount</th><th className="px-3 py-2 text-left font-semibold">Status</th></>}
                  {card.collection === 'accounts' && <><th className="px-3 py-2 text-left font-semibold">Name</th><th className="px-3 py-2 text-left font-semibold">City</th><th className="px-3 py-2 text-left font-semibold">Country</th></>}
                  {!['opportunities','invoices','accounts'].includes(card.collection) && <><th className="px-3 py-2 text-left font-semibold">Name</th><th className="px-3 py-2 text-right font-semibold">Value</th></>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.records || groups || []).slice(0, 15).map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleItemClick(r.name || r.label)}>
                    {card.collection === 'opportunities' && <>
                      <td className="px-3 py-2 text-gray-700">{r.account_name || '-'}</td>
                      <td className="px-3 py-2 text-gray-900 font-medium">{r.name || '-'}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900">{formatNumberCompact(r.sale_value || 0)}</td>
                      <td className="px-3 py-2 text-gray-600">{r.owner_name || '-'}</td>
                    </>}
                    {card.collection === 'invoices' && <>
                      <td className="px-3 py-2 text-gray-700">{r.account_name || r.partner_name || r.partner_id || '-'}</td>
                      <td className="px-3 py-2 text-gray-900 font-medium">{r.invoice_number || r.name || '-'}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900">{formatNumberCompact(r.amount_total || 0)}</td>
                      <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.payment_state === 'paid' ? 'bg-green-100 text-green-700' : r.payment_state === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{r.payment_state || '-'}</span></td>
                    </>}
                    {card.collection === 'accounts' && <>
                      <td className="px-3 py-2 text-gray-900 font-medium">{r.name || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.city || '-'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.country || '-'}</td>
                    </>}
                    {!['opportunities','invoices','accounts'].includes(card.collection) && <>
                      <td className="px-3 py-2 text-gray-900">{r.name || r.label || '-'}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{formatNumberCompact(r.sale_value || r.total || r.amount_total || 0)}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const sortBlocksByLayout = (blocks = []) => [...blocks].sort((a, b) => {
  const ay = a?.y ?? 0;
  const by = b?.y ?? 0;
  if (ay !== by) return ay - by;
  return (a?.x ?? 0) - (b?.x ?? 0);
});

function DashboardLayoutGrid({ blocks, onDrillDown, onNavigate, onItemNavigate, healthFilters }) {
  const orderedBlocks = sortBlocksByLayout(blocks);

  const renderBlock = (block) => {
    if (block.card?.display_type === 'health_card') {
      return <HealthCardRenderer type={block.card?.health_card_type} filters={healthFilters} />;
    }
    if (['number', 'win_rate'].includes(block.card?.display_type)) {
      return <KpiCard card={block.card} data={block.data} prevPeriod={block.prev_period} onDrillDown={onDrillDown} onNavigate={onNavigate} />;
    }
    return <ChartCard card={block.card} data={block.data} onDrillDown={onDrillDown} onNavigate={onNavigate} onItemNavigate={onItemNavigate} />;
  };

  return (
    <>
      <div className="hidden lg:grid grid-cols-12 auto-rows-[85px] gap-4" data-testid="dashboard-layout-grid-desktop">
        {orderedBlocks.map(block => {
          const colStart = (block.x ?? 0) + 1;
          const rowStart = (block.y ?? 0) + 1;
          const colSpan = Math.max(1, Math.min(12, block.w ?? (['number', 'win_rate'].includes(block.card?.display_type) ? 3 : 6)));
          const rowSpan = Math.max(1, block.h ?? (block.card?.display_type === 'health_card' ? 5 : ['number', 'win_rate'].includes(block.card?.display_type) ? 2 : 3));
          return (
            <div
              key={block.i || block.card_id}
              className="min-h-0"
              style={{ gridColumn: `${colStart} / span ${colSpan}`, gridRow: `${rowStart} / span ${rowSpan}` }}
              data-testid={`dashboard-layout-block-${block.card_id}`}
            >
              <div className="h-full min-h-0">{renderBlock(block)}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:hidden grid-cols-1 gap-4" data-testid="dashboard-layout-grid-mobile">
        {orderedBlocks.map(block => (
          <div
            key={`${block.i || block.card_id}-mobile`}
            className={block.card?.display_type === 'health_card' ? 'min-h-[420px]' : ['number', 'win_rate'].includes(block.card?.display_type) ? 'min-h-[140px]' : 'min-h-[300px]'}
            data-testid={`dashboard-layout-mobile-block-${block.card_id}`}
          >
            {renderBlock(block)}
          </div>
        ))}
      </div>
    </>
  );
}

// ============ MAIN READ-ONLY DASHBOARD ============
export default function HybridDashboard() {
  const [loading, setLoading] = useState(true);
  const [blocks, setBlocks] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [drillDown, setDrillDown] = useState({ open: false, card: null });
  const [activeSession, setActiveSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isCustomTab, setIsCustomTab] = useState(false);
  // Standard filters
  const [filterOptions, setFilterOptions] = useState({ years: [], salesReps: [], accounts: [], stages: [], productDirectors: [], solutionCategories: [] });
  const [filters, setFilters] = useState({ year: String(new Date().getFullYear()), quarter: null, salesRep: null, stage: null, productDirector: null, solutionCategory: null });
  // Slideshow mode
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [slideshowTemplates, setSlideshowTemplates] = useState([]);
  const [slideshowIdx, setSlideshowIdx] = useState(0);
  const navigate = useNavigate();

  // sessions ref — for reading in loadDashboard without causing re-renders
  const sessionsRef = useRef([]);

  const updateFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));
  const resetFilters = () => setFilters({ year: String(new Date().getFullYear()), quarter: null, salesRep: null, stage: null, productDirector: null, solutionCategory: null });

  // Load a specific template by ID, or fall back to role-based default
  const loadTemplate = useCallback(async (templateId = null) => {
    setLoading(true);
    try {
      const filterParams = {};
      if (filters.quarter) filterParams.quarter = filters.quarter;
      if (filters.salesRep) filterParams.salesperson = filters.salesRep;
      if (filters.productDirector) filterParams.product_director = filters.productDirector;
      if (filters.solutionCategory) filterParams.solution_category = filters.solutionCategory;
      if (filters.stage) filterParams.stage = filters.stage;
      let r;
      if (templateId) {
        r = await targetAPI.renderTemplate(templateId, filters.year || String(new Date().getFullYear()));
        // renderTemplate returns { template, cards, rendered_at } — need to convert cards → blocks
        const t = r.data.template;
        const cards = r.data.cards || [];
        const tb = t?.blocks || [];
        const enriched = tb.length > 0
          ? tb.map(b => { const m = cards.find(c => c.card_id === b.card_id); return { ...b, card: m?.card, data: m?.data }; })
          : cards.map((c, i) => ({
              i: c.card_id, x: (i % 4) * 3, y: Math.floor(i / 4), w: 3, h: 1,
              type: 'query_card', card_id: c.card_id, card: c.card, data: c.data,
            }));
        setBlocks(enriched);
        setTemplateName(t?.name || 'Dashboard');
      } else {
        r = await targetAPI.getMyDashboard(filters.year || String(new Date().getFullYear()), filterParams);
        setBlocks(r.data.blocks || []);
        setTemplateName(r.data.template?.name || 'Dashboard');
      }
    } catch (e) {
      console.error('Dashboard load error', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadDashboard = useCallback(() => {
    // Wait for sessions to load before attempting to render
    if (!activeSession || sessionsRef.current.length === 0) return;
    
    const session = sessionsRef.current.find(s => s.id === activeSession);
    if (session?.tab_type === 'custom') {
      setLoading(false);
      return;
    }
    if (session?.key) {
      setLoading(true);
      const filterParams = { session_key: session.key };
      if (filters.quarter) filterParams.quarter = filters.quarter;
      if (filters.salesRep) filterParams.salesperson = filters.salesRep;
      if (filters.productDirector) filterParams.product_director = filters.productDirector;
      if (filters.solutionCategory) filterParams.solution_category = filters.solutionCategory;
      if (filters.stage) filterParams.stage = filters.stage;
      targetAPI.getMyDashboard(filters.year || String(new Date().getFullYear()), filterParams)
        .then((r) => {
          setBlocks(r.data.blocks || []);
          setTemplateName(r.data.template?.name || session.name || 'Dashboard');
        })
        .catch((e) => console.error('Dashboard load error', e))
        .finally(() => setLoading(false));
    }
  }, [activeSession, filters.year, filters.quarter, filters.salesRep, filters.productDirector, filters.solutionCategory, filters.stage]); // ← sessions NOT in deps, use ref instead

  // Reload when filters OR active tab changes
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Handle sessions loaded — store in ref AND state, then trigger dashboard load
  const handleSessionsLoaded = useCallback((tabs) => {
    sessionsRef.current = tabs;
    setSessions(tabs);
    // Auto-select first tab if none selected
    if (!activeSession && tabs.length > 0) {
      const firstTab = tabs[0];
      setActiveSession(firstTab.id);
      setIsCustomTab(firstTab.tab_type === 'custom');
      // Directly trigger load since setActiveSession is async
      if (firstTab.tab_type !== 'custom' && firstTab.key) {
        setLoading(true);
        const filterParams = { session_key: firstTab.key };
        if (filters.quarter) filterParams.quarter = filters.quarter;
        if (filters.salesRep) filterParams.salesperson = filters.salesRep;
        if (filters.productDirector) filterParams.product_director = filters.productDirector;
        if (filters.solutionCategory) filterParams.solution_category = filters.solutionCategory;
        if (filters.stage) filterParams.stage = filters.stage;
        targetAPI.getMyDashboard(filters.year || String(new Date().getFullYear()), filterParams)
          .then((r) => {
            setBlocks(r.data.blocks || []);
            setTemplateName(r.data.template?.name || firstTab.name || 'Dashboard');
          })
          .catch((e) => console.error('Dashboard load error', e))
          .finally(() => setLoading(false));
      }
    } else {
      // Reload with fresh session data (template links may have changed)
      const session = tabs.find(s => s.id === activeSession);
      if (session?.tab_type === 'custom') {
        setIsCustomTab(true);
        setLoading(false);
      } else if (session?.key) {
        setIsCustomTab(false);
        loadDashboard();
      }
    }
  }, [activeSession, loadDashboard]);

  // Handle tab switch
  const handleSessionChange = (sessionId) => {
    setActiveSession(sessionId);
    const session = sessionsRef.current.find(s => s.id === sessionId);
    setIsCustomTab(session?.tab_type === 'custom');
  };

  // Load filter options (same API as Opportunities page for consistency)
  useEffect(() => {
    (async () => {
      try {
        const [fRes, pmRes, catRes] = await Promise.allSettled([
          analyticsAPI.getFilters(),
          targetAPI.getProductManagers(),
          targetAPI.getSolutionCategories(),
        ]);
        const fData = fRes.status === 'fulfilled' ? fRes.value.data : {};
        const pms = pmRes.status === 'fulfilled' ? pmRes.value.data : [];
        const cats = catRes.status === 'fulfilled' ? catRes.value.data : [];
        setFilterOptions({
          years: fData.years || [],
          salesReps: fData.sales_reps || fData.salesReps || [],
          accounts: (fData.accounts || []).map(a => typeof a === 'object' ? a.name : a).filter(Boolean),
          stages: fData.stages || [],
          productDirectors: pms.map(p => p.name),
          solutionCategories: cats.map(c => c.name),
        });
      } catch {}
    })();
  }, []);

  // Slideshow auto-rotate between templates
  useEffect(() => {
    if (!slideshowActive || slideshowTemplates.length < 2) return;
    const interval = setInterval(async () => {
      const nextIdx = (slideshowIdx + 1) % slideshowTemplates.length;
      setSlideshowIdx(nextIdx);
      const tpl = slideshowTemplates[nextIdx];
      try {
        const r = await targetAPI.renderTemplate(tpl.id, filters.year);
        const t = r.data.template;
        const cards = r.data.cards || [];
        const tb = t.blocks || [];
        const enriched = tb.length > 0
          ? tb.map(b => { const m = cards.find(c => c.card_id === b.card_id); return { ...b, card: m?.card, data: m?.data }; })
          : cards.map((c, i) => ({ i: c.card_id, x: (i % 6) * 2, y: Math.floor(i / 6), w: 2, h: 1, type: 'query_card', card_id: c.card_id, card: c.card, data: c.data }));
        setBlocks(enriched);
        setTemplateName(t.name);
      } catch {}
    }, 10000); // 10 seconds per template
    return () => clearInterval(interval);
  }, [slideshowActive, slideshowIdx, slideshowTemplates, filters.year]);


  const handleCardNavigate = (card) => {
    if (!card) return;
    const params = new URLSearchParams();
    if (filters.year) params.set('year', filters.year);
    if (card.filters) {
      const f = typeof card.filters === 'string' ? JSON.parse(card.filters) : card.filters;
      if (f.stage === 'Won') params.set('stage', 'Won');
      else if (f.stage === 'Lost') params.set('stage', 'Lost');
      else if (f.stage?.$nin) params.set('stage_exclude', f.stage.$nin.join(','));
    }
    if (filters.salesRep) params.set('salesRep', filters.salesRep);
    if (filters.productDirector) params.set('productDirector', filters.productDirector);
    if (filters.solutionCategory) params.set('solutionCategory', filters.solutionCategory);

    if (card.collection === 'opportunities') navigate(`/opportunities?${params.toString()}`);
    else if (card.collection === 'invoices') navigate('/invoices');
    else if (card.collection === 'accounts') navigate('/accounts');
    else if (card.collection === 'activities') navigate('/activities');
  };

  // Navigate with a specific item filter (e.g., click Nabisaheb → /opportunities?salesRep=Nabisaheb&stage=Won)
  const handleItemNavigate = (card, groupLabel) => {
    if (!card || !groupLabel) return;
    const params = new URLSearchParams();
    if (filters.year) params.set('year', filters.year);
    const groupBy = card.group_by;
    if (groupBy === 'owner_name') params.set('salesRep', groupLabel);
    else if (groupBy === 'product_manager') params.set('productDirector', groupLabel);
    else if (groupBy === 'solution_category') params.set('solutionCategory', groupLabel);
    else if (groupBy === 'stage') params.set('stage', groupLabel);
    else if (groupBy === 'account_name') params.set('account', groupLabel);
    else if (groupBy === 'team_name') params.set('team', groupLabel);
    if (card.filters) {
      const f = typeof card.filters === 'string' ? JSON.parse(card.filters) : card.filters;
      if (f.stage === 'Won' && groupBy !== 'stage') params.set('stage', 'Won');
      else if (f.stage === 'Lost' && groupBy !== 'stage') params.set('stage', 'Lost');
    }
    if (card.collection === 'opportunities') navigate(`/opportunities?${params.toString()}`);
    else if (card.collection === 'invoices') navigate('/invoices');
    else if (card.collection === 'accounts') navigate('/accounts');
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generating Excel export...');
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      if (filters.year) params.set('year', filters.year);
      if (filters.salesRep) params.set('sales_rep', filters.salesRep);
      if (filters.productDirector) params.set('product_director', filters.productDirector);
      if (filters.solutionCategory) params.set('solution_category', filters.solutionCategory);
      if (filters.stage) params.set('stage', filters.stage);
      if (filters.quarter) params.set('quarter', filters.quarter);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateName.replace(/\s+/g, '_')}_${filters.year || 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Excel export downloaded!');
    } catch (e) {
      toast.error('Export failed');
      console.error(e);
    }
  };

  const isKpi = (b) => ['number', 'win_rate'].includes(b.card?.display_type);
  const kpiBlocks = blocks.filter(b => isKpi(b));
  const chartBlocks = blocks.filter(b => !isKpi(b));

  // ── Render ── (always render tabs, never unmount them with early return)
  return (
    <div className="space-y-3" data-testid="dashboard-page">
      {/* Dashboard Session Tabs — ALWAYS mounted so they don't reload on each render */}
      <DashboardSessionTabs
        activeTab={activeSession}
        onTabChange={handleSessionChange}
        onSessionsLoaded={handleSessionsLoaded}
        isAdmin={true}
      />

      {/* Custom tab — shows card picker + user's custom layout */}
      {isCustomTab ? (
        <div className="space-y-3">
          <CustomDashboardTab
            year={filters.year}
            onRenderBlocks={(customBlocks) => {
              setBlocks(customBlocks);
              setTemplateName('My Custom');
              setLoading(false);
            }}
          />
          {/* Render custom blocks using the same card renderers */}
          {blocks.length > 0 && (() => {
            return (
              <>
                <DashboardLayoutGrid
                  blocks={blocks}
                  onDrillDown={c => setDrillDown({ open: true, card: c })}
                  onNavigate={handleCardNavigate}
                  onItemNavigate={handleItemNavigate}
                  healthFilters={filters}
                />
              </>
            );
          })()}
          <DrillDownPanel open={drillDown.open} onClose={() => setDrillDown({ open: false, card: null })} card={drillDown.card} year={filters.year} onNavigate={handleCardNavigate} />
        </div>
      ) : loading ? (
        /* Loading skeleton shown inside the container, tabs stay mounted */
        <div className="space-y-5" data-testid="dashboard-loading">
          <div className="flex justify-between"><Skeleton className="h-8 w-48" /><Skeleton className="h-9 w-40" /></div>
          <div className="grid grid-cols-6 gap-3">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>
          <div className="grid grid-cols-2 gap-4"><Skeleton className="h-56 rounded-lg" /><Skeleton className="h-56 rounded-lg" /></div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="dashboard-title">{templateName}</h1>
              {blocks.length > 0 && <p className="text-sm text-muted-foreground">Showing filtered data ({kpiBlocks.length} KPIs, {chartBlocks.length} charts)</p>}
            </div>
            <div className="flex items-center gap-2">
              <RoleScopeControl dataTestId="dashboard-role-scope-control" />
              <Button variant="outline" size="sm" onClick={loadDashboard} data-testid="refresh-btn"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
              <Button variant={slideshowActive ? "default" : "outline"} size="sm" data-testid="slideshow-btn"
                className={slideshowActive ? "bg-[#800000] hover:bg-[#9a1919] text-white" : ""}
                onClick={async () => {
                  if (slideshowActive) { setSlideshowActive(false); return; }
                  try { const r = await targetAPI.listTemplates(); setSlideshowTemplates(r.data); setSlideshowIdx(0); setSlideshowActive(true); } catch {}
                }}>
                {slideshowActive ? <><Pause className="h-4 w-4 mr-1" /> Stop</> : <><Play className="h-4 w-4 mr-1" /> Slideshow</>}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> Export Excel
              </Button>
            </div>
          </div>

          {/* Standard Inline Filter Bar */}
          <PageFilters
            title="Dashboard Filters"
            onReset={resetFilters}
            activeFilters={[filters.year, filters.quarter, filters.salesRep, filters.stage, filters.productDirector, filters.solutionCategory]}
          >
            <YearFilter value={filters.year} onChange={v => updateFilter('year', v)} years={filterOptions.years} />
            <QuarterFilter value={filters.quarter} onChange={v => updateFilter('quarter', v)} />
            <SalesRepFilter value={filters.salesRep} onChange={v => updateFilter('salesRep', v)} salesReps={filterOptions.salesReps} />
            <StageFilter value={filters.stage} onChange={v => updateFilter('stage', v)} stages={filterOptions.stages} />
            <ProductDirectorFilter value={filters.productDirector} onChange={v => updateFilter('productDirector', v)} productDirectors={filterOptions.productDirectors} />
            <SolutionCategoryFilter value={filters.solutionCategory} onChange={v => updateFilter('solutionCategory', v)} categories={filterOptions.solutionCategories} />
          </PageFilters>

          {blocks.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <Layers className="h-16 w-16 mx-auto mb-4 text-gray-200" />
              <h3 className="text-lg font-semibold text-gray-400">No Dashboard Configured</h3>
              <p className="text-sm text-gray-300 mt-1">Ask your admin to configure a dashboard in the Dashboard Builder</p>
            </div>
          ) : (
            <>
              <DashboardLayoutGrid
                blocks={blocks}
                onDrillDown={c => setDrillDown({ open: true, card: c })}
                onNavigate={handleCardNavigate}
                onItemNavigate={handleItemNavigate}
                healthFilters={filters}
              />
            </>
          )}
          <DrillDownPanel open={drillDown.open} onClose={() => setDrillDown({ open: false, card: null })} card={drillDown.card} year={filters.year} onNavigate={handleCardNavigate} />
        </>
      )}
    </div>
  );
}
